import React from 'react';
import { IDataRequest, IResearchTeamMemberDetail } from '@vivli/features/data-requests/infrastructure/interface';
import { FormProvider } from 'react-hook-form';
import { TeamMemberFormComponent } from './team-member-form.component';
import {
    useResearcherSubtitle,
    useResearchTeamMemberForm,
    useResearchTeamPermissions
} from '@vivli/features/data-requests/infrastructure/hook';
import { TeamMemberModalButtonsComponent } from './team-member-modal-buttons.component';

const wrapperStyle = {
    padding: '15px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgb(233, 234, 239)'
} as React.CSSProperties;

interface TeamMemberModalComponentProps {
    teamMember: IResearchTeamMemberDetail;
    readonly?: boolean;
    isLoading?: boolean;
    onCancel?: () => void;
    onSave?: (updatedTeamMember: IResearchTeamMemberDetail) => void;
    onDelete?: (teamMember: IResearchTeamMemberDetail) => void;
    dataRequest: IDataRequest;
    isAdminEditing?: boolean;
}

export const TeamMemberModalComponent = ({
    teamMember,
    onCancel,
    onSave,
    onDelete,
    isLoading,
    dataRequest,
    readonly,
    isAdminEditing
}: TeamMemberModalComponentProps) => {
    // isAdminEditing enables the Vivli-admin edit-mode branch of userCanEdit (any lifecycle); the
    // request-admin branch stays Draft-only inside the hook, so this doesn't widen request-admin access.
    const { userCanEdit, userCanDelete } = useResearchTeamPermissions(dataRequest, isAdminEditing);
    const formApi = useResearchTeamMemberForm(teamMember);
    const getSubtitle = useResearcherSubtitle();

    const handleSaveClick = () => {
        formApi.handleSubmit((formValues) => {
            const updatedTeamMember = { ...teamMember, ...formValues };
            onSave && onSave(updatedTeamMember);
        })();
    };

    const subTitle = getSubtitle(teamMember);

    return (
        <div style={wrapperStyle}>
            <FormProvider {...formApi}>
                <TeamMemberFormComponent
                    isLoading={isLoading}
                    dataRequest={dataRequest}
                    subTitle={subTitle}
                    readonly={readonly || !userCanEdit}
                    teamMember={teamMember}
                    userCanEdit={userCanEdit}
                />
            </FormProvider>

            <TeamMemberModalButtonsComponent
                onCancel={onCancel}
                onSave={handleSaveClick}
                onDelete={() => onDelete && onDelete(teamMember)}
                showSaveButton={userCanEdit && !readonly}
                showDeleteButton={
                    userCanDelete &&
                    !readonly &&
                    !!onDelete &&
                    !teamMember.isLeadResearcher &&
                    !teamMember.isStatistician &&
                    !teamMember.isAdmin
                }
                isLoading={isLoading}
            />
        </div>
    );
};
